// askSmart-ui/src/pages/TOSP/tabs/BuildingsTab.tsx
import React, { useMemo, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import InfoModal from "../../../components/InfoModal";
import BuildingModal, { BuildingFormValues } from "../components/BuildingModal";
import BuildingDetailsModal from "../components/BuildingDetailsModal";
import { formatCurrency } from "../format";
import { camByBuilding, effectiveLines } from "../budgetAllocation";
import { useTospData } from "../TospDataContext";
import { Building, COMMERCIAL_SPACE_TYPE, newId, unitBudgetTotal } from "../types";
import { sameText } from "../validation";
import { useGridSort } from "../useGridSort";

// Shared by the header and every row so the columns can't drift apart.
const GRID_COLUMNS = "1.8fr 1.2fr 0.8fr 1fr 1fr 1.4fr";

type SortCol = "name" | "nickname" | "gsccNumber" | "camPsf" | "taxPsf";

// What a building costs per square foot, which is the figure quoted to a
// prospective tenant — the annual totals beside it answered nothing anybody
// asks, since a big building costs more than a small one by definition.
const SORT_COLUMNS: { col: SortCol; label: string }[] = [
  { col: "name", label: "Building Name" },
  { col: "nickname", label: "Nickname" },
  { col: "gsccNumber", label: "GSCC #" },
  { col: "camPsf", label: "Avg CAM PSF" },
  { col: "taxPsf", label: "Avg Tax PSF" },
];

/** A rate per square foot, to the cent. Null where there is nothing to divide. */
const psf = (value: number | null): string =>
  value === null
    ? "—"
    : `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Buildings — the properties an org manages in TOSP. Units, and through them
// tenants, reference these by id.
export default function BuildingsTab() {
  const { buildings, saveBuilding, deleteBuilding, units, tenants, taxRolls, budgets } =
    useTospData();
  // The building being edited, `null` for a new one, `undefined` when closed —
  // so "add" and "edit" share one modal without a second open/closed flag.
  const [editing, setEditing] = useState<Building | null | undefined>(undefined);
  const [viewing, setViewing] = useState<Building | null>(null);
  const [deleting, setDeleting] = useState<Building | null>(null);
  const [blockedDelete, setBlockedDelete] = useState<string | null>(null);

  /**
   * A building's COMMERCIAL area — storage, basement and patio excluded.
   *
   * Every psf figure in the module is quoted against commercial space, because
   * that is what a tenant is comparing when they ask what a unit costs. Counting
   * the lockers would quietly report the building cheaper than it is.
   */
  const commercialSqFt = (buildingId: string): number =>
    units
      .filter(
        (unit) =>
          unit.buildingId === buildingId &&
          sameText(unit.spaceType, COMMERCIAL_SPACE_TYPE),
      )
      .reduce((total, unit) => total + (unit.occupancySqFt || 0), 0);

  /**
   * CAM per square foot, FROM THE BUDGET.
   *
   * It used to divide a figure typed on the building form — a placeholder from
   * before the budget produced CAM at all, and a field nothing has edited for
   * some time. Now each category's cost is divided across the units it reaches
   * and summed per building, which is the same arithmetic the statement's CAMS
   * line and the 41001 recovery are built on, so the three cannot disagree.
   *
   * Read for the LATEST budgeted year rather than for today, exactly as the tax
   * rate below is: a budget is worked up a year ahead, and asking for the
   * current calendar year would show nothing for most of the register.
   *
   * `camExpense` survives on the records that have one, unread. A building
   * whose units no budget reaches reads "—" rather than falling back to it:
   * a figure nobody maintains is worse than an honest blank.
   */
  const camBudgetYear = budgets
    .map((budget) => budget.year)
    .reduce<number | null>((latest, year) => (latest === null || year > latest ? year : latest), null);

  const camByBuildingId = useMemo(() => {
    const budget = budgets.find((candidate) => candidate.year === camBudgetYear);
    if (!budget) return new Map<string, number>();
    return camByBuilding(
      effectiveLines(budget.lines, budget.expenseOverrides),
      budget.splits,
      units,
      tenants,
      budget.year,
      // The occupancy the budget states, so a building's CAM per square foot
      // reads the same here as on the budget it came from.
      budget.camOccupancy,
    );
  }, [budgets, camBudgetYear, units, tenants]);

  const camPsf = (building: Building): number | null => {
    const area = commercialSqFt(building.id);
    const cam = camByBuildingId.get(building.id);
    if (!area || cam === undefined || !Number.isFinite(cam)) return null;
    return cam / area;
  };

  /**
   * Real estate tax per square foot, from the roll budgets.
   *
   * Read for the latest year anybody has budgeted rather than for today: rolls
   * are budgeted a year or two ahead, so asking for the current calendar year
   * would show nothing for most of the register.
   */
  const budgetYear = taxRolls
    .flatMap((roll) => (roll.budgets || []).map((entry) => entry.year))
    .reduce<number | null>((latest, year) => (latest === null || year > latest ? year : latest), null);

  const taxPsf = (building: Building): number | null => {
    if (budgetYear === null) return null;
    const area = commercialSqFt(building.id);
    if (!area) return null;
    const annual = units
      .filter(
        (unit) =>
          unit.buildingId === building.id &&
          sameText(unit.spaceType, COMMERCIAL_SPACE_TYPE),
      )
      .reduce((total, unit) => total + (unitBudgetTotal(taxRolls, unit.id, budgetYear) ?? 0), 0);
    return annual === 0 ? null : annual / area;
  };

  const { sortButton, sortRows } = useGridSort<SortCol, Building>("name", (building, col) =>
    col === "camPsf"
      ? (camPsf(building) ?? -1)
      : col === "taxPsf"
        ? (taxPsf(building) ?? -1)
        : building[col] || "",
  );

  // Saves write one record; the modal stays open if the save fails, so nothing
  // is lost to a dropped connection.
  const handleSave = async (values: BuildingFormValues) => {
    try {
      await saveBuilding({
        ...(editing || { id: newId("bld") }),
        name: values.name,
        nickname: values.nickname || undefined,
        gsccNumber: values.gsccNumber || undefined,
      });
      setEditing(undefined);
    } catch {
      /* surfaced by the provider */
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteBuilding(deleting.id);
      setDeleting(null);
    } catch {
      /* surfaced by the provider */
    }
  };

  /**
   * A building holding units can't be deleted.
   *
   * Units point at it by id, so removing it would strand every one of them —
   * and with them their tax rolls, tax years and occupancies. Told rather than
   * greyed out: a disabled button says no without saying why, and the fix is
   * on another tab.
   */
  const requestDelete = (building: Building) => {
    const held = units.filter((unit) => unit.buildingId === building.id);
    if (held.length > 0) {
      const named = held
        .slice(0, 5)
        .map((unit) => unit.suiteNumber)
        .join(", ");
      setBlockedDelete(
        `"${building.name}" can't be deleted because it still holds ${held.length} unit${
          held.length === 1 ? "" : "s"
        } — ${named}${held.length > 5 ? `, and ${held.length - 5} more` : ""}. Move or delete them on the Units tab first.`,
      );
      return;
    }
    setDeleting(building);
  };

  return (
    <section className="tosp-card">
      <div className="tosp-toolbar">
        <button className="tosp-btn-primary" onClick={() => setEditing(null)}>
          + Add Building
        </button>
      </div>

      <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
        {SORT_COLUMNS.map(({ col, label }) => (
          <div key={col}>{sortButton(col, label)}</div>
        ))}
        <div>Actions</div>
      </div>

      {buildings.length === 0 && (
        <div className="tosp-grid-empty">
          No buildings yet. Click "+ Add Building" to create your first building.
        </div>
      )}

      {sortRows(buildings).map((building) => (
        <div key={building.id} className="tosp-grid-row" style={{ gridTemplateColumns: GRID_COLUMNS }}>
          <div>{building.name}</div>
          <div>{building.nickname || "—"}</div>
          <div>{building.gsccNumber || "—"}</div>
          <div
            title={
              camPsf(building) === null
                ? "No budget reaches this building's units, or there is no commercial area to divide across"
                : `${formatCurrency(camByBuildingId.get(building.id) ?? 0)} of ${camBudgetYear} CAM across ${commercialSqFt(
                    building.id,
                  ).toLocaleString()} sq ft of commercial space`
            }
          >
            {psf(camPsf(building))}
          </div>
          <div
            title={
              taxPsf(building) === null
                ? "No tax budget entered for these units yet"
                : `${budgetYear} roll budgets across ${commercialSqFt(
                    building.id,
                  ).toLocaleString()} sq ft of commercial space`
            }
          >
            {psf(taxPsf(building))}
          </div>
          <div className="tosp-actions">
            <button
              className="tosp-btn-icon tosp-btn-view"
              title="View"
              onClick={() => setViewing(building)}
            >
              👁
            </button>
            <button
              className="tosp-btn-icon tosp-btn-edit"
              title="Edit"
              onClick={() => setEditing(building)}
            >
              ✎
            </button>
            <button
              className="tosp-btn-icon tosp-btn-delete"
              title="Delete"
              onClick={() => requestDelete(building)}
            >
              🗑
            </button>
          </div>
        </div>
      ))}

      <BuildingModal
        isOpen={editing !== undefined}
        building={editing ?? null}
        buildings={buildings}
        onClose={() => setEditing(undefined)}
        onSave={handleSave}
      />

      <BuildingDetailsModal building={viewing} onClose={() => setViewing(null)} />

      <InfoModal
        isOpen={!!blockedDelete}
        title="Building In Use"
        message={blockedDelete || ""}
        onClose={() => setBlockedDelete(null)}
      />

      <DeleteConfirmModal
        isOpen={!!deleting}
        title="Delete Building"
        message={`Are you sure you want to delete "${deleting?.name}"? This cannot be undone.`}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
